import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  type WebhookConfig,
  type WebhookConfigCreateRequest,
  type WebhookConfigUpdateRequest,
  type WebhookProviderSpec,
  type WebhookSourceStatus,
  type WebhookTemplate,
  type WebhookTemplateCreateRequest,
  type WebhookTemplateTestRequest,
  type WebhookTemplateTestResponse,
  type WebhookTemplateUpdateRequest,
} from "@/lib/api";

const WEBHOOK_CONFIGS_KEY = ["webhook-configs"] as const;
const WEBHOOK_PROVIDERS_KEY = ["webhook-providers"] as const;
const WEBHOOK_STATUS_KEY = ["webhook-status"] as const;
const WEBHOOK_TEMPLATES_KEY = ["webhook-templates"] as const;

export function useWebhookStatuses() {
  return useQuery<WebhookSourceStatus[]>({
    queryKey: WEBHOOK_STATUS_KEY,
    queryFn: () => apiGet<WebhookSourceStatus[]>("/api/v1/webhooks"),
  });
}

export function useWebhookConfigs() {
  return useQuery<WebhookConfig[]>({
    queryKey: WEBHOOK_CONFIGS_KEY,
    queryFn: () => apiGet<WebhookConfig[]>("/api/v1/webhooks/configs"),
  });
}

export function useWebhookProviderSpecs() {
  return useQuery<WebhookProviderSpec[]>({
    queryKey: WEBHOOK_PROVIDERS_KEY,
    queryFn: () => apiGet<WebhookProviderSpec[]>("/api/v1/webhooks/providers"),
  });
}

export function useWebhookTemplates(provider?: string) {
  return useQuery<WebhookTemplate[]>({
    queryKey: provider ? [...WEBHOOK_TEMPLATES_KEY, provider] : WEBHOOK_TEMPLATES_KEY,
    queryFn: () =>
      apiGet<WebhookTemplate[]>("/api/v1/webhooks/templates", provider ? { provider } : undefined),
  });
}

export function useCreateWebhookConfig() {
  const qc = useQueryClient();
  return useMutation<WebhookConfig, Error, WebhookConfigCreateRequest>({
    mutationFn: (body) => apiPost<WebhookConfig>("/api/v1/webhooks/configs", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOK_CONFIGS_KEY });
      qc.invalidateQueries({ queryKey: WEBHOOK_STATUS_KEY });
    },
  });
}

export function useUpdateWebhookConfig() {
  const qc = useQueryClient();
  return useMutation<
    WebhookConfig,
    Error,
    { id: string; patch: WebhookConfigUpdateRequest }
  >({
    mutationFn: ({ id, patch }) =>
      apiPatch<WebhookConfig>(`/api/v1/webhooks/configs/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOK_CONFIGS_KEY });
      qc.invalidateQueries({ queryKey: WEBHOOK_STATUS_KEY });
    },
  });
}

export function useCreateWebhookTemplate() {
  const qc = useQueryClient();
  return useMutation<WebhookTemplate, Error, WebhookTemplateCreateRequest>({
    mutationFn: (body) => apiPost<WebhookTemplate>("/api/v1/webhooks/templates", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOK_TEMPLATES_KEY });
      qc.invalidateQueries({ queryKey: WEBHOOK_PROVIDERS_KEY });
    },
  });
}

export function useUpdateWebhookTemplate() {
  const qc = useQueryClient();
  return useMutation<
    WebhookTemplate,
    Error,
    { id: string; patch: WebhookTemplateUpdateRequest }
  >({
    mutationFn: ({ id, patch }) =>
      apiPatch<WebhookTemplate>(`/api/v1/webhooks/templates/${id}`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOK_TEMPLATES_KEY });
      qc.invalidateQueries({ queryKey: WEBHOOK_PROVIDERS_KEY });
    },
  });
}

export function useDeleteWebhookTemplate() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiDelete(`/api/v1/webhooks/templates/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOK_TEMPLATES_KEY });
      qc.invalidateQueries({ queryKey: WEBHOOK_PROVIDERS_KEY });
    },
  });
}

export function useTestWebhookTemplate() {
  return useMutation<WebhookTemplateTestResponse, Error, WebhookTemplateTestRequest>({
    mutationFn: (body) => apiPost<WebhookTemplateTestResponse>("/api/v1/webhooks/templates/test", body),
  });
}

export function useDeleteWebhookConfig() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiDelete(`/api/v1/webhooks/configs/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: WEBHOOK_CONFIGS_KEY });
      qc.invalidateQueries({ queryKey: WEBHOOK_STATUS_KEY });
    },
  });
}
